import { CHUNK_SIZE, VOXELS_PER_METRE } from "../sim/world/constants";
import {
  CAVE_GRID_OFFSET,
  CAVE_GRID_SIZE,
  COAST_BLEND_END,
  SURFACE_EROSION_RANGE,
  WORLD_EXTENT,
  voxels,
} from "./constants";
import { hashFloat, hashSigned, smoothstep, valueNoise3 } from "./random";
import { alphaRegionGraph } from "./regions";
import { terrainHeight } from "./terrain";
import type { CaveEntrance, CavePass, RegionGraph } from "./types";

interface Point3 {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

interface CurveDistance {
  readonly squared: number;
  readonly amount: number;
}

interface WormCurve {
  readonly points: readonly Point3[];
  readonly minX: number;
  readonly maxX: number;
  readonly minY: number;
  readonly maxY: number;
  readonly minZ: number;
  readonly maxZ: number;
}

interface CheeseRoom {
  readonly site: Point3;
  readonly radiusX: number;
  readonly radiusY: number;
  readonly radiusZ: number;
}

interface EntranceSite {
  readonly gridX: number;
  readonly gridZ: number;
  readonly site: Point3;
}

export interface CaveSampler {
  passAt(x: number, y: number, z: number, surfaceY: number): CavePass;
  /**
   * Dense x-major pass codes for generation: 0 solid, 1 worm, 2 cavern,
   * 3 entrance. `null` when this sub-chunk touches no cave feature at all.
   */
  sampleVolume(minY: number, surfaces: Int16Array, step?: number): Uint8Array | null;
}

export const CAVE_PASS_CODE = Object.freeze({ solid: 0, worm: 1, cavern: 2, entrance: 3 } as const);

/**
 * The bezier is sampled as a ten-segment polyline. The count is deliberately
 * *not* rescaled: curve length, worm radius and polyline sagitta all grew by
 * the same factor, so ten segments describe exactly the same shape as before —
 * and raising it would multiply the rasteriser's per-chunk cost for nothing.
 */
const WORM_SAMPLES = 10;
/** Lowest and highest voxel the cave network may occupy. */
export const CAVE_FLOOR = voxels(5);
export const CAVE_CEILING = voxels(58);
/** Caves never break the surface except through an authored entrance shaft. */
const CAVE_SURFACE_CLEARANCE = voxels(4);
/** Worm half-width, in voxels: a ~5 m passage, as the cave reference reads. */
const WORM_RADIUS = voxels(2.65);
const WORM_RADIUS_LIMIT = voxels(4.05);

/** Noise is sampled in metres so cave wavelengths survive the rescale. */
function metres(value: number): number {
  return value / VOXELS_PER_METRE;
}

/** Jittered Worley feature point; all three coordinates move with the seed. */
function worleySite(seed: number, gridX: number, gridZ: number): Point3 {
  const baseX = CAVE_GRID_OFFSET + gridX * CAVE_GRID_SIZE;
  const baseZ = CAVE_GRID_OFFSET + gridZ * CAVE_GRID_SIZE;
  return {
    x: baseX + hashSigned(seed ^ 0x243f6a88, gridX, 0, gridZ) * CAVE_GRID_SIZE * 0.27,
    y: voxels(22) + hashFloat(seed ^ 0xb5297a4d, gridX, 0, gridZ) * voxels(22),
    z: baseZ + hashSigned(seed ^ 0x85a308d3, gridX, 0, gridZ) * CAVE_GRID_SIZE * 0.27,
  };
}

function caveCell(value: number): number {
  return Math.floor((value - CAVE_GRID_OFFSET) / CAVE_GRID_SIZE);
}

function curveControl(seed: number, from: Point3, to: Point3, gridX: number, gridZ: number, axis: 0 | 1): readonly [Point3, Point3] {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const dz = to.z - from.z;
  const lateral = CAVE_GRID_SIZE * 0.19;
  const vertical = voxels(8);
  return [{
    x: from.x + dx / 3 + hashSigned(seed ^ 0x13198a2e, gridX, axis, gridZ) * lateral,
    y: from.y + dy / 3 + hashSigned(seed ^ 0xa4093822, gridX, axis, gridZ) * vertical,
    z: from.z + dz / 3 + hashSigned(seed ^ 0x299f31d0, gridX, axis, gridZ) * lateral,
  }, {
    x: from.x + dx * 2 / 3 + hashSigned(seed ^ 0x082efa98, gridX, axis, gridZ) * lateral,
    y: from.y + dy * 2 / 3 + hashSigned(seed ^ 0xec4e6c89, gridX, axis, gridZ) * vertical,
    z: from.z + dz * 2 / 3 + hashSigned(seed ^ 0x452821e6, gridX, axis, gridZ) * lateral,
  }];
}

function bezierPoint(from: Point3, first: Point3, second: Point3, to: Point3, amount: number): Point3 {
  const inverse = 1 - amount;
  const a = inverse ** 3;
  const b = 3 * inverse * inverse * amount;
  const c = 3 * inverse * amount * amount;
  const d = amount ** 3;
  return {
    x: from.x * a + first.x * b + second.x * c + to.x * d,
    y: from.y * a + first.y * b + second.y * c + to.y * d,
    z: from.z * a + first.z * b + second.z * c + to.z * d,
  };
}

function sampledCurve(from: Point3, first: Point3, second: Point3, to: Point3): readonly Point3[] {
  const points: Point3[] = [];
  for (let sample = 0; sample <= WORM_SAMPLES; sample += 1) {
    points.push(bezierPoint(from, first, second, to, sample / WORM_SAMPLES));
  }
  return Object.freeze(points);
}

function pointSegmentDistance(x: number, y: number, z: number, from: Point3, to: Point3): CurveDistance {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const dz = to.z - from.z;
  const lengthSquared = dx * dx + dy * dy + dz * dz;
  const amount = lengthSquared === 0 ? 0 : Math.max(0, Math.min(1,
    ((x - from.x) * dx + (y - from.y) * dy + (z - from.z) * dz) / lengthSquared,
  ));
  const offsetX = from.x + dx * amount - x;
  const offsetY = from.y + dy * amount - y;
  const offsetZ = from.z + dz * amount - z;
  return { squared: offsetX * offsetX + offsetY * offsetY + offsetZ * offsetZ, amount };
}

function curveDistance(x: number, y: number, z: number, points: readonly Point3[]): CurveDistance {
  let bestSquared = Number.POSITIVE_INFINITY;
  let bestAmount = 0;
  for (let index = 1; index < points.length; index += 1) {
    const from = points[index - 1];
    const to = points[index];
    if (!from || !to) continue;
    const candidate = pointSegmentDistance(x, y, z, from, to);
    if (candidate.squared < bestSquared) {
      bestSquared = candidate.squared;
      bestAmount = (index - 1 + candidate.amount) / WORM_SAMPLES;
    }
  }
  return { squared: bestSquared, amount: bestAmount };
}

function isWorleyWorm(seed: number, curves: readonly WormCurve[], x: number, y: number, z: number): boolean {
  for (const curve of curves) {
    if (x < curve.minX || x > curve.maxX || y < curve.minY || y > curve.maxY
      || z < curve.minZ || z > curve.maxZ) continue;
    const distance = curveDistance(x, y, z, curve.points);
    if (distance.squared > WORM_RADIUS_LIMIT ** 2) continue;
    const radiusNoise = valueNoise3(seed ^ 0x6d2b79f5, metres(x) / 17, metres(y) / 13, metres(z) / 17);
    const taper = Math.sin(distance.amount * Math.PI);
    const radius = WORM_RADIUS + taper * voxels(0.75) + radiusNoise * voxels(0.35);
    if (distance.squared <= radius * radius) return true;
  }
  return false;
}

/** Organic 3D threshold fields around selected Worley sites form cheese rooms. */
function isCheeseCavern(seed: number, rooms: readonly CheeseRoom[], x: number, y: number, z: number): boolean {
  for (const room of rooms) {
    if (Math.abs(x - room.site.x) > room.radiusX * 1.08
      || Math.abs(y - room.site.y) > room.radiusY * 1.08
      || Math.abs(z - room.site.z) > room.radiusZ * 1.08) continue;
    const dx = (x - room.site.x) / room.radiusX;
    const dy = (y - room.site.y) / room.radiusY;
    const dz = (z - room.site.z) / room.radiusZ;
    const envelope = dx * dx + dy * dy + dz * dz;
    if (envelope >= 1.15) continue;
    const broad = valueNoise3(seed ^ 0x7f4a7c15, metres(x) / 11, metres(y) / 8, metres(z) / 11);
    const pores = valueNoise3(seed ^ 0x94d049bb, metres(x) / 4.5, metres(y) / 4.5, metres(z) / 4.5);
    const density = broad * 0.72 + pores * 0.28;
    if (envelope < 0.16 || density > envelope * 0.72 - 0.64) return true;
  }
  return false;
}

function hasEntrance(seed: number, gridX: number, gridZ: number): boolean {
  return (gridX + gridZ + (seed >>> 0)) % 5 === 0;
}

function entranceCenter(seed: number, entrance: EntranceSite, surfaceY: number, amount: number): Point3 {
  const bottom = entrance.site;
  const top = { x: Math.round(bottom.x), y: surfaceY, z: Math.round(bottom.z) };
  const height = Math.max(1, top.y - bottom.y);
  const first = {
    x: bottom.x + hashSigned(seed ^ 0xd1b54a32, entrance.gridX, 0, entrance.gridZ) * voxels(7),
    y: bottom.y + height * 0.34,
    z: bottom.z + hashSigned(seed ^ 0xca5a8263, entrance.gridX, 0, entrance.gridZ) * voxels(7),
  };
  const second = {
    x: top.x + hashSigned(seed ^ 0x8cb92baa, entrance.gridX, 0, entrance.gridZ) * voxels(5),
    y: bottom.y + height * 0.7,
    z: top.z + hashSigned(seed ^ 0x4f1bbcdc, entrance.gridX, 0, entrance.gridZ) * voxels(5),
  };
  return bezierPoint(bottom, first, second, top, amount);
}

/**
 * An entrance shaft is carved a little *above* the smooth surface so the eroded
 * surface layer cannot cap it back over.
 *
 * Has to clear the *full* erosion swing, not just the broad octave's amplitude:
 * a shaft cut two voxels above the smooth height is sealed by any column the
 * finer octaves pushed three or four voxels up, and a sealed entrance is the only
 * way into the cave network.
 */
function entranceTop(surfaceY: number): number {
  return surfaceY + SURFACE_EROSION_RANGE;
}

function isEntrance(seed: number, entrances: readonly EntranceSite[], x: number, y: number, z: number, surfaceY: number): boolean {
  for (const entrance of entrances) {
    if (y < entrance.site.y - voxels(3) || y > entranceTop(surfaceY)) continue;
    const amount = Math.max(0, Math.min(1, (y - entrance.site.y) / Math.max(1, surfaceY - entrance.site.y)));
    const center = entranceCenter(seed, entrance, surfaceY, amount);
    const radius = voxels(2.7) + smoothstep(0.72, 1, amount) * voxels(0.9);
    const dx = x - center.x;
    const dz = z - center.z;
    if (dx * dx + dz * dz <= radius * radius) return true;
  }
  return false;
}

/**
 * Rasterizes into the sub-chunk's own sampling lattice. `step` is the LOD
 * stride in voxels: lattice index `i` on an axis is world coordinate
 * `min + i * step`, so a 4x-downsampled chunk covers four times the world for
 * the same 32³ of storage. Returns `null` when nothing was carved, which lets
 * the caller skip a 32 768-voxel merge pass for the overwhelming majority of
 * sub-chunks — most of a 2 048-voxel-tall column is nowhere near a cave.
 */
function rasterizeCaves(
  seed: number,
  curves: readonly WormCurve[],
  rooms: readonly CheeseRoom[],
  entrances: readonly EntranceSite[],
  minX: number,
  minY: number,
  minZ: number,
  step: number,
  surfaces: Int16Array,
): Uint8Array | null {
  if (surfaces.length !== CHUNK_SIZE * CHUNK_SIZE) throw new RangeError("cave surface volume has the wrong dimensions");
  const maxX = minX + (CHUNK_SIZE - 1) * step;
  const maxY = minY + (CHUNK_SIZE - 1) * step;
  const maxZ = minZ + (CHUNK_SIZE - 1) * step;
  let passes: Uint8Array | null = null;
  const volumeIndex = (ix: number, iy: number, iz: number): number =>
    ix + CHUNK_SIZE * (iz + CHUNK_SIZE * iy);
  const surfaceAt = (ix: number, iz: number): number => surfaces[ix + iz * CHUNK_SIZE] ?? 0;
  const carve = (ix: number, iy: number, iz: number, pass: number): void => {
    passes ??= new Uint8Array(CHUNK_SIZE ** 3);
    passes[volumeIndex(ix, iy, iz)] = pass;
  };
  const first = (min: number, world: number): number => Math.max(0, Math.ceil((world - min) / step));
  const last = (min: number, world: number): number =>
    Math.min(CHUNK_SIZE - 1, Math.floor((world - min) / step));
  const underground = (x: number, y: number, z: number, surface: number): boolean =>
    x >= 0 && z >= 0 && x < WORLD_EXTENT && z < WORLD_EXTENT
    && y >= CAVE_FLOOR && y <= Math.min(CAVE_CEILING, surface - CAVE_SURFACE_CLEARANCE);

  // Rasterizing each short curve segment visits only its narrow voxel envelope;
  // generation no longer scans every curve for every voxel in the sub-chunk.
  for (const curve of curves) {
    if (curve.maxY < minY || curve.minY > maxY || curve.maxX < minX || curve.minX > maxX
      || curve.maxZ < minZ || curve.minZ > maxZ) continue;
    for (let segment = 1; segment < curve.points.length; segment += 1) {
      const from = curve.points[segment - 1];
      const to = curve.points[segment];
      if (!from || !to) continue;
      const limit = WORM_RADIUS_LIMIT;
      const firstX = first(minX, Math.min(from.x, to.x) - limit);
      const lastX = last(minX, Math.max(from.x, to.x) + limit);
      const firstY = first(minY, Math.min(from.y, to.y) - limit);
      const lastY = last(minY, Math.max(from.y, to.y) + limit);
      const firstZ = first(minZ, Math.min(from.z, to.z) - limit);
      const lastZ = last(minZ, Math.max(from.z, to.z) + limit);
      for (let iy = firstY; iy <= lastY; iy += 1) {
        const y = minY + iy * step;
        for (let iz = firstZ; iz <= lastZ; iz += 1) {
          const z = minZ + iz * step;
          for (let ix = firstX; ix <= lastX; ix += 1) {
            const x = minX + ix * step;
            if (!underground(x, y, z, surfaceAt(ix, iz))) continue;
            const distance = pointSegmentDistance(x, y, z, from, to);
            if (distance.squared > limit * limit) continue;
            const amount = (segment - 1 + distance.amount) / WORM_SAMPLES;
            const radiusNoise = valueNoise3(seed ^ 0x6d2b79f5, metres(x) / 17, metres(y) / 13, metres(z) / 17);
            const radius = WORM_RADIUS + Math.sin(amount * Math.PI) * voxels(0.75) + radiusNoise * voxels(0.35);
            if (distance.squared <= radius * radius) carve(ix, iy, iz, CAVE_PASS_CODE.worm);
          }
        }
      }
    }
  }

  for (const room of rooms) {
    const firstX = first(minX, room.site.x - room.radiusX * 1.08);
    const lastX = last(minX, room.site.x + room.radiusX * 1.08);
    const firstY = first(minY, room.site.y - room.radiusY * 1.08);
    const lastY = last(minY, room.site.y + room.radiusY * 1.08);
    const firstZ = first(minZ, room.site.z - room.radiusZ * 1.08);
    const lastZ = last(minZ, room.site.z + room.radiusZ * 1.08);
    for (let iy = firstY; iy <= lastY; iy += 1) {
      const y = minY + iy * step;
      for (let iz = firstZ; iz <= lastZ; iz += 1) {
        const z = minZ + iz * step;
        for (let ix = firstX; ix <= lastX; ix += 1) {
          const x = minX + ix * step;
          if (underground(x, y, z, surfaceAt(ix, iz)) && isCheeseCavern(seed, [room], x, y, z)) {
            carve(ix, iy, iz, CAVE_PASS_CODE.cavern);
          }
        }
      }
    }
  }

  for (const entrance of entrances) {
    const reach = voxels(12);
    const firstX = first(minX, entrance.site.x - reach);
    const lastX = last(minX, entrance.site.x + reach);
    const firstZ = first(minZ, entrance.site.z - reach);
    const lastZ = last(minZ, entrance.site.z + reach);
    const firstY = first(minY, entrance.site.y - voxels(3));
    for (let iy = firstY; iy < CHUNK_SIZE; iy += 1) {
      const y = minY + iy * step;
      for (let iz = firstZ; iz <= lastZ; iz += 1) {
        const z = minZ + iz * step;
        for (let ix = firstX; ix <= lastX; ix += 1) {
          const x = minX + ix * step;
          const surface = surfaceAt(ix, iz);
          if (y <= entranceTop(surface) && isEntrance(seed, [entrance], x, y, z, surface)) {
            carve(ix, iy, iz, CAVE_PASS_CODE.entrance);
          }
        }
      }
    }
  }
  return passes;
}

/** Precomputes immutable cave features intersecting a horizontal query area. */
export function createCaveSampler(seed: number, minX: number, maxX: number, minZ: number, maxZ: number): CaveSampler {
  const normalizedSeed = seed >>> 0;
  const firstGridX = caveCell(minX) - 2;
  const lastGridX = caveCell(maxX) + 2;
  const firstGridZ = caveCell(minZ) - 2;
  const lastGridZ = caveCell(maxZ) + 2;
  const curves: WormCurve[] = [];
  const rooms: CheeseRoom[] = [];
  const entrances: EntranceSite[] = [];
  for (let gridZ = firstGridZ; gridZ <= lastGridZ; gridZ += 1) {
    for (let gridX = firstGridX; gridX <= lastGridX; gridX += 1) {
      const site = worleySite(normalizedSeed, gridX, gridZ);
      const radiusX = voxels(10) + hashFloat(normalizedSeed ^ 0x1b56c4e9, gridX, 0, gridZ) * voxels(7);
      const radiusZ = voxels(10) + hashFloat(normalizedSeed ^ 0x9e3779b9, gridX, 0, gridZ) * voxels(7);
      const entranceReach = voxels(35);
      if (hashFloat(normalizedSeed ^ 0x68e31da4, gridX, 0, gridZ) <= 0.58
        && site.x + radiusX >= minX && site.x - radiusX <= maxX
        && site.z + radiusZ >= minZ && site.z - radiusZ <= maxZ) {
        rooms.push({
          site,
          radiusX,
          radiusY: voxels(6) + hashFloat(normalizedSeed ^ 0x38d01377, gridX, 0, gridZ) * voxels(5),
          radiusZ,
        });
      }
      if (hasEntrance(normalizedSeed, gridX, gridZ)
        && site.x + entranceReach >= minX && site.x - entranceReach <= maxX
        && site.z + entranceReach >= minZ && site.z - entranceReach <= maxZ) entrances.push({ gridX, gridZ, site });
      for (const axis of [0, 1] as const) {
        const to = worleySite(normalizedSeed, gridX + (axis === 0 ? 1 : 0), gridZ + (axis === 1 ? 1 : 0));
        const [first, second] = curveControl(normalizedSeed, site, to, gridX, gridZ, axis);
        const points = sampledCurve(site, first, second, to);
        const curveMinX = Math.min(...points.map((point) => point.x));
        const curveMaxX = Math.max(...points.map((point) => point.x));
        const curveMinZ = Math.min(...points.map((point) => point.z));
        const curveMaxZ = Math.max(...points.map((point) => point.z));
        const curveMinY = Math.min(...points.map((point) => point.y));
        const curveMaxY = Math.max(...points.map((point) => point.y));
        const margin = voxels(5);
        if (curveMaxX + margin >= minX && curveMinX - margin <= maxX
          && curveMaxZ + margin >= minZ && curveMinZ - margin <= maxZ) {
          curves.push({
            points,
            minX: curveMinX - margin,
            maxX: curveMaxX + margin,
            minY: curveMinY - margin,
            maxY: curveMaxY + margin,
            minZ: curveMinZ - margin,
            maxZ: curveMaxZ + margin,
          });
        }
      }
    }
  }
  return Object.freeze({
    passAt(x: number, y: number, z: number, surfaceY: number): CavePass {
      if (y < CAVE_FLOOR || y > surfaceY || x < 0 || z < 0 || x >= WORLD_EXTENT || z >= WORLD_EXTENT) return "solid";
      if (isEntrance(normalizedSeed, entrances, x, y, z, surfaceY)) return "entrance";
      if (y > Math.min(CAVE_CEILING, surfaceY - CAVE_SURFACE_CLEARANCE)) return "solid";
      if (isCheeseCavern(normalizedSeed, rooms, x, y, z)) return "cavern";
      if (isWorleyWorm(normalizedSeed, curves, x, y, z)) return "worm";
      return "solid";
    },
    sampleVolume(minY: number, surfaces: Int16Array, step = 1): Uint8Array | null {
      return rasterizeCaves(normalizedSeed, curves, rooms, entrances, minX, minY, minZ, step, surfaces);
    },
  });
}

const pointSamplerCache = new Map<string, CaveSampler>();

/** Point-query convenience API; repeated local queries reuse immutable features. */
export function cavePassAt(seed: number, x: number, y: number, z: number, surfaceY: number): CavePass {
  const cellX = Math.floor(x / 32);
  const cellZ = Math.floor(z / 32);
  const key = `${seed >>> 0},${cellX},${cellZ}`;
  let sampler = pointSamplerCache.get(key);
  if (!sampler) {
    sampler = createCaveSampler(seed, cellX * 32, cellX * 32 + 31, cellZ * 32, cellZ * 32 + 31);
    pointSamplerCache.set(key, sampler);
    if (pointSamplerCache.size > 64) {
      const oldest = pointSamplerCache.keys().next().value as string | undefined;
      if (oldest !== undefined) pointSamplerCache.delete(oldest);
    }
  }
  return sampler.passAt(x, y, z, surfaceY);
}

/** Every returned connector starts at a connected graph site and opens at the surface. */
export function caveEntrances(seed: number, graph: RegionGraph = alphaRegionGraph(seed)): readonly CaveEntrance[] {
  const normalizedSeed = seed >>> 0;
  const cached = entranceCache.get(graph);
  if (cached?.seed === normalizedSeed) return cached.entrances;
  const entrances: CaveEntrance[] = [];
  const last = Math.ceil((WORLD_EXTENT - CAVE_GRID_OFFSET) / CAVE_GRID_SIZE);
  for (let gridZ = -1; gridZ <= last; gridZ += 1) {
    for (let gridX = -1; gridX <= last; gridX += 1) {
      if (!hasEntrance(normalizedSeed, gridX, gridZ)) continue;
      const site = worleySite(normalizedSeed, gridX, gridZ);
      const x = Math.round(site.x);
      const z = Math.round(site.z);
      if (x < COAST_BLEND_END || z < COAST_BLEND_END
        || x >= WORLD_EXTENT - COAST_BLEND_END || z >= WORLD_EXTENT - COAST_BLEND_END) continue;
      entrances.push(Object.freeze({
        x,
        z,
        surfaceY: terrainHeight(normalizedSeed, x, z, graph),
        networkY: Math.round(site.y),
      }));
    }
  }
  const frozen = Object.freeze(entrances);
  entranceCache.set(graph, { seed: normalizedSeed, entrances: frozen });
  return frozen;
}

const entranceCache = new WeakMap<RegionGraph, { readonly seed: number; readonly entrances: readonly CaveEntrance[] }>();
